import React, { useEffect, useState, useMemo } from "react";
import { IonRow, IonCol, IonToast, useIonToast, IonicSafeString } from "@ionic/react";
import Button from "./Button";
import { ApplicationState } from "../store";
import { useSelector, useDispatch } from "react-redux";
import * as DealStore from '../store/DealStore';
import * as BasketStore from '../store/BasketStore';
import { bindActionCreators } from "redux";
import { useHistory, useLocation } from "react-router";
import { shouldShowModifierGroup, getSelectedSkus } from "../helpers/menuHelpers";

function MopFooterDeal() {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const menuItem = useSelector((state: ApplicationState) => state.menuItemData?.menuItem);
    const isBasket = useSelector((state: ApplicationState) => state.menuItemData?.isBasket);
    const currentPage = useSelector((state: ApplicationState) => state.deal?.currentPage);

    const cancelDeal = bindActionCreators(DealStore.actionCreators.cancelDeal, dispatch);
    const setCurrentPage = bindActionCreators(DealStore.actionCreators.setCurrentPage, dispatch);
    const addToBasket = bindActionCreators(BasketStore.actionCreators.addMenuItemToBasket, dispatch);
    const updateBasket = bindActionCreators(BasketStore.actionCreators.updateBasketItem, dispatch);

    const selectedSkus = useMemo(() => {
        if (!menuItem?.modifierGroups) {
            return [];
        }

        return getSelectedSkus(menuItem.modifierGroups);
    }, [menuItem?.modifierGroups, currentPage])

    const pages = useMemo(() => {
        if (!menuItem?.modifierGroups) {
            return [];
        }

        const pages = menuItem.modifierGroups
            .filter((group: IMopModifierGroup) => shouldShowModifierGroup(group, selectedSkus, menuItem.sku))
            .map((group: IMopModifierGroup) => group.showOnPage);

        const unique = new Set(pages);

        return Array.from(unique).sort();
    }, [menuItem?.modifierGroups, selectedSkus]);

    const lastPage = useMemo(() => {
        return pages.slice(-1)[0];
    }, [pages]);

    const [enableButton, setEnableButton] = useState(false);

    const [isGreyedOut, setIsGreyedOut] = useState(false);
    const [requiredItems, setRequiredItems] = useState(0);
    const [showToastError, setShowToastError] = useState(false);
    const [showToastSuccess, setShowToastSuccess] = useState(false);

    function handleBack() {
        if (currentPage === 1) {
            cancelDeal();
        }

        history.goBack();
    }

    // HACK: history.go(-pages.size) does not update the UI.
    // This might lead to issues on slow phones where it shows each page.
    function goBackRecursive(to: string) {
        history.goBack();

        setTimeout(() => {
            if (!history.location.pathname.includes(to)) {
                goBackRecursive(to);
            } else {
                return;
            }
        }, 100);
    }

    function completeDeal() {
        if (isBasket) {
            updateBasket();
            goBackRecursive('basket');
        } else {
            addToBasket();
            goBackRecursive('categories');
        }
    }

    function getFirstValidPage(page: number) {
        let validPage = page;

        while (validPage < lastPage && pages.indexOf(validPage) === -1) {
            validPage += 1;
        }

        return validPage;
    }

    function goToNextPage() {
        let nextPage = (currentPage ?? 1) + 1;

        nextPage = getFirstValidPage(nextPage);

        let pathname = location.pathname;
        const params = new URLSearchParams();
        params.set('page', nextPage.toString());

        if (pages.indexOf(nextPage) === -1) {
            pathname = '/deal-summary';
            params.set('count', (pages.length + 1).toString())
        } else {
            params.set('count', (pages.indexOf(nextPage) + 1).toString());
        }

        history.push(`${pathname}?${params.toString()}`);
    }

    function handleNext() {
        if (isGreyedOut) {
            setShowToastError(true);
            return;
        }

        setShowToastError(false);
        setShowToastSuccess(false);

        if ((currentPage ?? 1) > lastPage) {
            completeDeal();
            return;
        }

        goToNextPage();
    }

    // Use the current page to set the global state.
    useEffect(() => {
        const params = new URLSearchParams(location.search.substr(1));
        const page = params.get('page')

        let validPage = 0;

        if (page) {
            validPage = parseInt(page, 10);
        }

        validPage = getFirstValidPage(validPage);

        setCurrentPage(validPage);
    }, [location.search, setCurrentPage]);

    const modifierGroups = useMemo(() => {
        return menuItem?.modifierGroups
            .filter((group: IMopModifierGroup) => (
                group.showOnPage === currentPage
            ));
    }, [menuItem, currentPage]);

    useEffect(() => {
        if (!modifierGroups) {
            return
        }

        const isPageComplete = modifierGroups.every((group: IMopModifierGroup) => {
            if (group.groupType === 10) {
                return group.modifierGroupItems.some((item: IMopModifierGroupItem) => (
                    item.quantitySelected > 0
                ));
            }

            return true;
        })

        const hasPriceSet = (menuItem?.menuItemTotalPrice ?? 0) > 0;

        setEnableButton(isPageComplete && hasPriceSet);
    }, [modifierGroups, menuItem?.menuItemTotalPrice]);

    useEffect(() => {
        if (!modifierGroups) {
            return;
        }

        const modifierGroup = modifierGroups.find((group: IMopModifierGroup) => {
            if (group.isMandatory && group.canSelectMultiple && group.maxDifferentItems && group.maxDifferentItems > 1) {
                return group;
            }

            return null;
        })

        if (!modifierGroup) {
            return;
        }

        setRequiredItems(modifierGroup.maxDifferentItems);

        const quantitySelected = modifierGroup.modifierGroupItems.reduce((acc: number, item: IMopModifierGroupItem) => (
            acc + item.quantitySelected
        ), 0)

        if (quantitySelected === modifierGroup.maxDifferentItems) {
            setIsGreyedOut(false);
            setShowToastError(false);
            setShowToastSuccess(true);
        } else {
            setIsGreyedOut(true);
            setShowToastSuccess(false);
        }
    }, [modifierGroups])

    return (
        <div className='MopFooterInner'>
            <IonRow className="ion-align-items-center">
                <IonCol size="6">
                    <Button
                        onClick={handleBack}
                        expand="block"
                        color="light"
                        rounded
                    >
                        {currentPage === 1
                            ?
                            <p>Cancel</p>
                            :
                            <p>Back</p>
                        }
                    </Button>
                </IonCol>
                <IonCol
                    size="6"
                    style={{
                        opacity: isGreyedOut ? 0.5 : 'inherit',
                    }}
                >
                    <Button
                        onClick={handleNext}
                        expand="block"
                        color="secondary"
                        disabled={!enableButton}
                        rounded
                    >
                        {(currentPage ?? 1) > lastPage
                            ?
                            <>
                                {isBasket
                                    ?
                                    <p>Update order</p>
                                    :
                                    <p>Add to order</p>
                                }
                            </>
                            :
                            <p>Next</p>
                        }
                    </Button>
                </IonCol>
            </IonRow>
            <IonToast
                animated={true}
                duration={2000}
                isOpen={showToastError}
                position={"bottom"}
                cssClass={"toastStyle"}
                message={`You need to add all ${requiredItems} items first`}
                color="success"
                onDidDismiss={() => setShowToastError(false)}
            />
            <IonToast
                animated={true}
                duration={2000}
                isOpen={showToastSuccess}
                position={"bottom"}
                cssClass={"toastStyle"}
                message={`Tap Next to continue`}
                color="success"
                onDidDismiss={() => setShowToastSuccess(false)}
            />
        </div>
    )
}

export default MopFooterDeal;
